import { ProgressBar } from '@/components/ui';
import { IconAlert, IconCheck, IconEdit, IconSend, IconTrash } from '@/components/Icons';
import { formatBRL, formatDate, formatISODateTime } from '@/lib/format';
import { DIRECTION_LABELS, formatBp, OPERATION_STATUS_LABELS } from '@/lib/leverage';
import type { LeverageAllocation, LeverageOperation } from '@/lib/types';
import { AccountCell } from './AccountCell';

interface OperationCardProps {
  operation: LeverageOperation;
  /** A marcação desta linha está a caminho do servidor. */
  pendingAllocationId: string | null;
  onToggleExecuted: (allocation: LeverageAllocation) => void;
  onEdit: () => void;
  onClose: () => void;
  onReopen: () => void;
  onDelete: () => void;
  /** Manda o sinal para a corretora; `again` quando ele já saiu antes. */
  onSendSignal: (again: boolean) => void;
  /** O envio do sinal desta operação está a caminho. */
  sendingSignal: boolean;
}

/** Instante real (ISO) no relógio de quem olha: "02/10 às 18:14". */
const formatInstant = (iso: string) => {
  const date = new Date(iso);
  return `${date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

/** A situação do sinal na corretora e o botão de enviar. */
function SignalStrip({ operation, onSend, sending }: { operation: LeverageOperation; onSend: (again: boolean) => void; sending: boolean }) {
  const { signal } = operation;
  const open = operation.status === 'OPEN';
  const sent = Boolean(signal?.sentAt);
  const state = signal?.error ? 'error' : signal?.stale ? 'stale' : sent ? 'sent' : 'none';

  const text = {
    none: 'Sinal ainda não enviado para a corretora.',
    sent: `Sinal enviado ${signal?.sentAt ? formatInstant(signal.sentAt) : ''}.`,
    stale: 'Ativo, direção ou hora mudaram depois do envio: a corretora está com o sinal antigo.',
    error: signal?.error ?? '',
  }[state];

  return (
    <div className="lev-signal" data-state={state} role={state === 'error' ? 'alert' : undefined}>
      <span className="lev-signal-icon" aria-hidden>
        {state === 'sent' ? <IconCheck /> : state === 'none' ? <IconSend /> : <IconAlert />}
      </span>
      <span className="lev-signal-text">
        {text}
        {state === 'error' && sent && signal?.sentAt ? <span className="muted"> O último sinal certo saiu {formatInstant(signal.sentAt)}.</span> : null}
      </span>
      {open ? (
        <button
          type="button"
          className={state === 'sent' ? 'btn btn-ghost btn-sm' : 'btn btn-primary btn-sm'}
          onClick={() => onSend(sent)}
          disabled={sending}
        >
          {sending ? <span className="spinner" /> : <IconSend />}
          {state === 'none' ? 'Enviar sinal' : state === 'error' && !sent ? 'Tentar de novo' : 'Enviar de novo'}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Uma operação é a lista para executar: para cada cliente, quanto entra e a
 * conta na corretora, e o visto de quando a ordem foi feita lá.
 */
export function OperationCard({
  operation, pendingAllocationId, onToggleExecuted, onEdit, onClose, onReopen, onDelete, onSendSignal, sendingSignal,
}: OperationCardProps) {
  const { totals } = operation;
  const open = operation.status === 'OPEN';
  const done = totals.clients > 0 && totals.executed === totals.clients;

  return (
    <article className="card lev-op" data-status={operation.status} aria-labelledby={`lev-op-${operation.id}`}>
      <header className="lev-op-head">
        <div style={{ minWidth: 0 }}>
          <div className="lev-op-title" id={`lev-op-${operation.id}`}>
            <span className="lev-symbol">{operation.symbol}</span>
            <span className="lev-dir" data-direction={operation.direction}>{DIRECTION_LABELS[operation.direction]}</span>
            <span className={open ? 'chip chip-primary' : 'chip'}>{OPERATION_STATUS_LABELS[operation.status]}</span>
          </div>
          <div className="lev-op-meta muted">
            <span className="num">{formatDate(operation.date)} às {operation.time}</span>
            <span aria-hidden>·</span>
            <span className="num">expira em {operation.expiryMinutes} min</span>
            {operation.tiers.length > 0 ? (
              <>
                <span aria-hidden>·</span>
                <span>
                  Faixas: {operation.tiers.map((tier) => `${formatBRL(tier.minCapital)}+ → ${formatBp(tier.percentBp)}`).join(' · ')}
                </span>
              </>
            ) : null}
            {operation.note ? (
              <>
                <span aria-hidden>·</span>
                <span className="truncate" style={{ maxWidth: 360 }}>{operation.note}</span>
              </>
            ) : null}
          </div>
        </div>
        <div className="lev-op-figures">
          <div className="lev-op-figure">
            <span className="label">A entrar</span>
            <span className="value num">{formatBRL(totals.amount)}</span>
          </div>
          <div className="lev-op-figure">
            <span className="label">Clientes</span>
            <span className="value num">{totals.clients}</span>
          </div>
        </div>
      </header>

      {operation.status === 'OPEN' || operation.signal ? (
        <SignalStrip operation={operation} onSend={onSendSignal} sending={sendingSignal} />
      ) : null}

      <div className="lev-op-progress">
        <ProgressBar value={totals.executed} max={totals.clients} color={done ? 'var(--success)' : undefined} />
        <span className="num muted nowrap">{totals.executed}/{totals.clients} executados</span>
      </div>

      <div className="table-wrap">
        <table className="tbl tbl-cards lev-exec">
          <thead>
            <tr>
              <th className="lev-exec-check"><span className="sr-only">Executado</span></th>
              <th>Cliente</th>
              <th className="right">%</th>
              <th className="right">Valor a entrar</th>
              <th>Conta na corretora</th>
            </tr>
          </thead>
          <tbody>
            {operation.allocations.map((allocation) => {
              const executed = Boolean(allocation.executedAt);
              return (
                <tr key={allocation.id} data-executed={executed ? 'true' : undefined}>
                  <td className="lev-exec-check">
                    <label className="lev-exec-toggle" title={executed ? 'Desmarcar' : 'Marcar como executado'}>
                      <input
                        type="checkbox"
                        checked={executed}
                        disabled={pendingAllocationId === allocation.id}
                        onChange={() => onToggleExecuted(allocation)}
                        aria-label={`${allocation.lead.name}: ${executed ? 'executado, desmarcar' : 'marcar como executado'}`}
                      />
                    </label>
                  </td>
                  <td className="cell-main">
                    <div className="lev-exec-name">{allocation.lead.name}</div>
                    <div className="muted lev-exec-sub">
                      {executed
                        ? `Executado ${formatISODateTime(allocation.executedAt)}${allocation.executedByName ? ` · ${allocation.executedByName}` : ''}`
                        : allocation.lead.ownerName ?? 'Sem responsável'}
                    </div>
                  </td>
                  <td className="num right nowrap" data-label="%">
                    {formatBp(allocation.percentBp)}
                    <div className="muted lev-exec-sub">de {formatBRL(allocation.capital)}</div>
                  </td>
                  <td className="num right nowrap cell-key lev-exec-amount" data-label="Valor a entrar">{formatBRL(allocation.amount)}</td>
                  <td data-label="Conta" className="cell-full">
                    <AccountCell lead={allocation.lead} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className="lev-op-foot">
        <span className="muted">
          {open
            ? done
              ? 'Todos executados. Encerre quando a operação acabar: a fatia volta a ficar livre.'
              : 'Marque cada cliente quando a ordem for feita na conta dele.'
            : `Encerrada${operation.closedAt ? ` em ${formatISODateTime(operation.closedAt)}` : ''}.`}
        </span>
        <div className="spacer" />
        {totals.executed === 0 ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onDelete}>
            <IconTrash /> Excluir
          </button>
        ) : null}
        {open ? (
          <>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Encerrar</button>
            <button type="button" className="btn btn-sm" onClick={onEdit}>
              <IconEdit /> Editar
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-sm" onClick={onReopen}>Reabrir</button>
        )}
      </footer>
    </article>
  );
}
